/**
 * SarahArchiver — 萨拉对话归档标记 + sendBeacon 触发
 *
 * 工作机制：
 * 1. 用户点"新话题" / 触发 idle 自动新话题 / 关闭浏览器前
 *    → markPending(convId) 把 conv 加入待归档队列
 * 2. flush() 用 navigator.sendBeacon 把队列 POST 给 /taro/ai/sarah/archive
 *    → 后端写 json + 调 Dify 生成 md
 * 3. localStorage 双保险：即使关闭浏览器前没调 flush，下次启动时再补发一次
 *
 * 关键设计：
 * - sendBeacon 是 fire-and-forget，但 Flarum 需要 CSRF token + JSON body
 *   我们用 Blob 包装 application/json + CSRF header
 * - 仅缓存当前用户的待归档队列（key = user_id）
 * - 一个 conv 只入队一次（Set 去重）
 */

const STORAGE_KEY_PREFIX = 'sarah_pending_archive_user_';
const API_PATH = '/taro/ai/sarah/archive';
// 24 小时未消费的视为过期（防止堆积）
const STALE_MS = 24 * 60 * 60 * 1000;

function storageKey(userId) {
  return STORAGE_KEY_PREFIX + userId;
}

/**
 * 读取队列（带过期清理）
 * @returns {string[]}
 */
function readQueue(userId) {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return [];
    const convs = Array.isArray(parsed.conversation_ids) ? parsed.conversation_ids : [];
    const updatedAt = parsed.updated_at || 0;
    if (Date.now() - updatedAt > STALE_MS) {
      // 过期清空
      localStorage.removeItem(storageKey(userId));
      return [];
    }
    return convs.filter((c) => typeof c === 'string' && c.length > 0);
  } catch (_) {
    return [];
  }
}

function writeQueue(userId, conversationIds) {
  try {
    localStorage.setItem(
      storageKey(userId),
      JSON.stringify({
        conversation_ids: conversationIds,
        updated_at: Date.now(),
      })
    );
  } catch (_) {
    /* 容量满 / 隐私模式 → 忽略 */
  }
}

/**
 * 把 convId 加入待归档队列（幂等）
 */
export function markPending(userId, conversationId) {
  if (!userId || !conversationId) return;
  const queue = readQueue(userId);
  if (queue.includes(conversationId)) return;
  queue.push(conversationId);
  // 单用户最多缓存 50 个
  const trimmed = queue.slice(-50);
  writeQueue(userId, trimmed);
}

/**
 * 移除已成功归档的 convId（按后端返回的 archived 列表）
 */
export function clearPending(userId, conversationIds) {
  if (!userId || !conversationIds || !conversationIds.length) return;
  const queue = readQueue(userId).filter((c) => !conversationIds.includes(c));
  writeQueue(userId, queue);
}

/**
 * 把队列发给后端（fire-and-forget）
 *
 * 用 sendBeacon：浏览器关闭时仍能完成请求
 * - Beacon 上限 64KB：50 个 conv_id 远小于
 * - 不阻塞关闭流程
 *
 * @returns {boolean} true 表示已发起，false 表示无队列或不支持
 */
export function flush(userId) {
  if (!userId) return false;
  const queue = readQueue(userId);
  if (queue.length === 0) return false;

  // sendBeacon 不支持自定义 header，但 Flarum 校验 CSRF 用 Cookie（XSRF-TOKEN），
  // 我们在 fetch 里把 X-CSRF-Token 头加上即可。
  // 先尝试 fetch keepalive（也能跨页面关闭发送），回退到 sendBeacon
  const apiUrl = window.app && window.app.forum && window.app.forum.attribute('apiUrl') || '';
  const url = apiUrl + API_PATH;
  const csrfToken =
    (window.app && window.app.session && window.app.session.csrfToken) || '';

  const body = JSON.stringify({
    data: {
      attributes: {
        conversation_ids: queue,
      },
    },
  });

  let sent = false;

  // 方式 1：fetch keepalive（最稳，能带 header）
  try {
    if (typeof fetch === 'function' && 'keepalive' in Request.prototype) {
      // 不 await：fire-and-forget
      fetch(url, {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: body,
      })
        .then(function (r) {
          if (r.ok) {
            return r.json().catch(function () { return null; });
          }
          return null;
        })
        .then(function (data) {
          if (data && data.data && Array.isArray(data.data.archived)) {
            clearPending(
              userId,
              data.data.archived.map(function (a) {
                return a.conversation_id;
              })
            );
          }
          // skipped + failed 也清掉（empty/skipped 不需要重试；failed 让它留在队列里下次重试）
          if (data && data.data && Array.isArray(data.data.skipped)) {
            clearPending(
              userId,
              data.data.skipped.map(function (s) {
                return s.conversation_id;
              })
            );
          }
        })
        .catch(function () {
          /* 静默失败，下次重试 */
        });
      sent = true;
    }
  } catch (_) {}

  // 方式 2：sendBeacon 兜底（无 header 也能发，但需要 Cookie-based CSRF）
  if (!sent && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    try {
      const blob = new Blob([body], { type: 'application/json' });
      sent = navigator.sendBeacon(url, blob);
    } catch (_) {
      sent = false;
    }
  }

  // 即便失败，**不要**清队列，等下次重试
  return sent;
}

/**
 * 启动时检查：上次关浏览器时是否有未归档的？补发一次
 */
export function bootFlush(userId) {
  if (!userId) return;
  const queue = readQueue(userId);
  if (queue.length === 0) return;
  // 稍等几秒，确保 Flarum app + session 已就绪
  setTimeout(function () {
    flush(userId);
  }, 2000);
}